import { useEffect, useMemo, useState } from 'react';
import { DataGrid } from '@/components/data-grid/core/DataGrid';
import { KeenIcon, Modal } from '@/components';
import { useSnackbar } from 'notistack';
import {
  useGetHarborDeletionRequestsQuery,
  useApproveHarborDeletionMutation,
  useRejectHarborDeletionMutation,
  useRecoverHarborMutation,
} from '@/services/HarborManagementService';
import {
  HarborDeletionRequest,
  HarborStatusType,
} from '@/types/harbor-management.types';
import { PaginationState } from '@tanstack/react-table';
import { ConfirmationDialog } from '@/components/dialogBox/ConfirmationDialog';
import { HarborDeletionViewModal } from './HarborDeletionViewModal';
import { formatInternationalPhoneNumber } from '@/utils';

type Row = HarborDeletionRequest;

export const HarborDeletionListings = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'pending' | 'deleted'>('pending');
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    request: HarborDeletionRequest | null;
  }>({ isOpen: false, request: null });

  const [confirmState, setConfirmState] = useState<{
    isOpen: boolean;
    id: string | null;
    action: 'approve' | 'reject' | 'recover' | null;
  }>({ isOpen: false, id: null, action: null });

  const { data, isLoading, isFetching, refetch } =
    useGetHarborDeletionRequestsQuery(
      {
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        search: debouncedSearchTerm.trim() || undefined,
        status: activeTab,
      },
      { refetchOnMountOrArgChange: true, refetchOnFocus: true }
    );

  const [approveHarborDeletion] = useApproveHarborDeletionMutation();
  const [rejectHarborDeletion] = useRejectHarborDeletionMutation();
  const [recoverHarbor] = useRecoverHarborMutation();

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    }, 500);
    return () => clearTimeout(t);
  }, [searchTerm, activeTab, pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    refetch();
  }, [
    debouncedSearchTerm,
    pagination.pageIndex,
    pagination.pageSize,
    activeTab,
    refetch,
  ]);

  const rows: Row[] = useMemo(() => data?.data || [], [data]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const resetFilters = () => {
    if (searchTerm) setSearchTerm('');
    if (activeTab !== 'pending') setActiveTab('pending');
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  };

  const onConfirm = async () => {
    if (!confirmState.id || !confirmState.action) return;
    try {
      let call;
      let successMessage = '';

      switch (confirmState.action) {
        case 'approve':
          call = approveHarborDeletion(confirmState.id);
          successMessage = 'Harbor deletion approved';
          break;
        case 'reject':
          call = rejectHarborDeletion(confirmState.id);
          successMessage = 'Harbor deletion rejected';
          break;
        case 'recover':
          call = recoverHarbor(confirmState.id);
          successMessage = 'Harbor recovered successfully';
          break;
        default:
          return;
      }

      if (call && typeof call.unwrap === 'function') {
        await call.unwrap();
      } else {
        await call;
      }

      enqueueSnackbar(successMessage, { variant: 'success' });
      setConfirmState({ isOpen: false, id: null, action: null });
      refetch();
    } catch (e: unknown) {
      const error = e as { data?: { message?: string } };
      enqueueSnackbar(error?.data?.message || 'Action failed', {
        variant: 'error',
      });
    }
  };

  const columns = useMemo(
    () => [
      {
        header: 'Harbor',
        accessorKey: 'name',
        cell: ({ row }: { row: { original: Row } }) => (
          <div>
            <div className="font-medium">{row.original.name}</div>
            <div className="text-sm text-gray-500">{row.original.location}</div>
          </div>
        ),
      },
      {
        header: 'Requested By',
        accessorKey: 'deletion_requested_by.name',
        cell: ({ row }: { row: { original: Row } }) => (
          <div>
            <div className="font-medium">
              {row.original.deletion_requested_by?.name}
            </div>
            <div className="text-sm text-gray-500">
              {row.original.deletion_requested_by?.email}
            </div>
          </div>
        ),
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ row }: { row: { original: Row } }) => (
          <span className="badge badge-warning dark:bg-yellow-900 badge-sm">
            {row.original.status === HarborStatusType.PENDING_DELETION
              ? 'Pending Deletion'
              : 'Deleted'}
          </span>
        ),
      },
      {
        header: 'Contact',
        accessorKey: 'contact_email',
        cell: ({ row }: { row: { original: Row } }) => (
          <div>
            <div className="text-sm">{row.original.contact_email}</div>
            <div className="text-sm text-gray-500">
              {row.original.contact_phone
                ? formatInternationalPhoneNumber(
                    row.original.contact_phone,
                    row.original.country_code,
                    (row.original as unknown as Record<string, unknown>)
                      .country_short_code as string | undefined
                  )
                : '-'}
            </div>
          </div>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: Row } }) => (
          <div className="flex items-center gap-2">
            <button
              onClick={() =>
                setModalState({
                  isOpen: true,
                  request: row.original,
                })
              }
              className="btn btn-sm btn-icon btn-light btn-outline"
              aria-label={`View deletion request for ${row.original.name}`}
            >
              <KeenIcon type="filled" icon="eye" />
            </button>

            {row.original.status === HarborStatusType.PENDING_DELETION && (
              <>
                <button
                  onClick={() =>
                    setConfirmState({
                      isOpen: true,
                      id: row.original.id,
                      action: 'approve',
                    })
                  }
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  aria-label={`Approve deletion of ${row.original.name}`}
                >
                  <KeenIcon type="filled" icon="check" />
                </button>
                <button
                  onClick={() =>
                    setConfirmState({
                      isOpen: true,
                      id: row.original.id,
                      action: 'reject',
                    })
                  }
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  aria-label={`Reject deletion of ${row.original.name}`}
                >
                  <KeenIcon type="filled" icon="cross" />
                </button>
              </>
            )}

            {/* Show recovery button for deleted harbors tab */}

            {activeTab === 'deleted' && (
              <button
                onClick={() =>
                  setConfirmState({
                    isOpen: true,
                    id: row.original.id,
                    action: 'recover',
                  })
                }
                className="btn btn-sm btn-icon btn-light btn-outline"
                aria-label={`Recover ${row.original.name}`}
              >
                <KeenIcon type="filled" icon="arrows-circle" />
              </button>
            )}
          </div>
        ),
        meta: { className: 'w-[200px]' },
      },
    ],
    [activeTab]
  );

  return (
    <div className="space-y-4">
      <div className="card card-grid min-w-full">
        {/* Tab Navigation */}
        <div className="border-b">
          <div className="flex">
            <button
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'pending'
                  ? 'border-primary text-primary contrast:text-blue-400  bg-primary/5'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => {
                setActiveTab('pending');
                setPagination((p) => ({ ...p, pageIndex: 0 }));
              }}
            >
              <span className="inline-flex items-center gap-3">
                <span>Pending Deletion Requests</span>
                {data?.totalItems && activeTab === 'pending' && (
                  <span className="px-2 py-1 text-xs bg-warning/20 text-warning contrast:text-blue-400 rounded-full">
                    {data.totalItems}
                  </span>
                )}
              </span>
            </button>
            <button
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'deleted'
                  ? 'border-primary text-primary contrast:text-blue-400 bg-primary/5'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => {
                setActiveTab('deleted');
                setPagination((p) => ({ ...p, pageIndex: 0 }));
              }}
            >
              <span className="inline-flex items-center gap-3">
                <span>Deleted Harbors (Recovery)</span>
                {data?.totalItems && activeTab === 'deleted' && (
                  <span className="px-2 py-1 text-xs bg-error/20 text-error rounded-full">
                    {data.totalItems}
                  </span>
                )}
              </span>
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 border-b">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <label className="input input-bordered input-sm flex items-center gap-2 h-8 sm:h-10">
              <KeenIcon
                type="solid"
                icon="magnifier"
                className="text-gray-500"
              />
              <input
                placeholder={`Search ${activeTab === 'pending' ? 'deletion requests' : 'deleted harbors'}`}
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                aria-label={`Search ${activeTab === 'pending' ? 'deletion requests' : 'deleted harbors'} by harbor name`}
              />
            </label>
          </div>

          {searchTerm !== '' && (
            <div className="flex items-center">
              <button
                className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10"
                onClick={resetFilters}
                aria-label="Clear search"
                title="Clear search"
              >
                <KeenIcon
                  type="solid"
                  icon="arrows-circle"
                  className="text-gray-600"
                />
                Clear Search
              </button>
            </div>
          )}
        </div>

        {/* Data Grid */}
        <div className="p-4">
          {isLoading || isFetching ? (
            <div className="text-center py-4">Loading...</div>
          ) : (
            <DataGrid
              columns={columns}
              data={rows}
              rowSelect={false}
              loading={isLoading || isFetching}
              pagination={{
                page: pagination.pageIndex,
                size: pagination.pageSize,
                more: true,
                totalItems: data?.totalItems || 0,
                totalPages: data?.totalPages,
                setPagination,
                info: `{from} - {to} of {count}`,
                sizes: [5, 10, 25, 50, 100],
                sizesLabel: 'Show',
                sizesDescription: 'per page',
              }}
              sorting={[{ id: 'requested_at', desc: true }]}
              serverSide={true}
              onFilterReset={resetFilters}
              hasActiveFilters={searchTerm !== ''}
            />
          )}
        </div>
      </div>

      {/* View Modal */}
      <Modal
        open={modalState.isOpen}
        onClose={() => setModalState({ isOpen: false, request: null })}
        title="Deletion Request Details"
        disableEnforceFocus={false}
      >
        {modalState.request ? (
          <HarborDeletionViewModal
            isOpen={modalState.isOpen}
            request={modalState.request}
            onClose={() => setModalState({ isOpen: false, request: null })}
          />
        ) : (
          <div className="p-4">Loading...</div>
        )}
      </Modal>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmState.isOpen}
        onClose={() =>
          setConfirmState({ isOpen: false, id: null, action: null })
        }
        onConfirm={onConfirm}
        message={
          confirmState.action === 'approve'
            ? 'Are you sure you want to approve this harbor deletion? The harbor will be soft-deleted and hidden from public view.'
            : confirmState.action === 'reject'
              ? 'Are you sure you want to reject this deletion request?'
              : confirmState.action === 'recover'
                ? 'Are you sure you want to recover this harbor? It will be restored and visible again.'
                : ''
        }
        confirmButtonText={
          confirmState.action === 'approve'
            ? 'Approve Deletion'
            : confirmState.action === 'reject'
              ? 'Reject Request'
              : confirmState.action === 'recover'
                ? 'Recover Harbor'
                : 'Confirm'
        }
      />
    </div>
  );
};
